import { useQueryNormalizer } from '@normy/react-query';
import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { resolveLabels } from '@/features/sources/lib/resolveLabels.lib';
import { gqlClient } from '@/lib';
import type { ExperienceFeed } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import type {
  UpdateFeedForExperienceMutation,
  UpdateFeedForExperienceMutationVariables,
} from './useUpdateFeed.generated';

const query = gql`
  mutation UpdateFeedForExperience($input: UpdateFeedForExperienceInput!) {
    updateFeedForExperience(input: $input) {
      id
      title
      originalTitle
      color
      emoji
      category {
        id
        text
        color
        colorName
        sortOrder
      }
      labels {
        id
        text
        sortOrder
      }
      whoLabels {
        id
        text
        sortOrder
      }
    }
  }
`;

export const useUpdateFeed = () => {
  const { experience } = usePageExperienceContext();
  const queryNormalizer = useQueryNormalizer();

  const { mutate, isPending } = useMutation<
    UpdateFeedForExperienceMutation,
    QueryError,
    UpdateFeedForExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateFeedForExperienceMutation,
        UpdateFeedForExperienceMutationVariables
      >(query, variables),
    onMutate: (variables) => {
      const { feedId, color, title, emoji, categoryId, labelIds, whoLabelIds } =
        variables.input;
      const prevValue = queryNormalizer.getObjectById<ExperienceFeed>(feedId);

      // Color/title resets (null) skip optimistic updates — let the server
      // settle the row with the original values. A null categoryId or emoji is
      // a real unassign, so both are applied optimistically below.
      if (color === null || title === null) {
        return { rollbackData: prevValue };
      }

      // Resolve the selected category so the assigned name/color reflect
      // immediately (the normalized feed stores `category`, not `categoryId`).
      const resolveCategory = () => {
        if (categoryId === undefined) return undefined;
        if (categoryId === null) return null;
        return (
          experience.categories.find((c) => c.id === categoryId) ?? undefined
        );
      };
      const category = resolveCategory();
      const labels = resolveLabels(labelIds, experience.labels);
      const whoLabels = resolveLabels(whoLabelIds, experience.whoLabels);

      return {
        rollbackData: prevValue,
        optimisticData: {
          id: feedId,
          ...(color !== undefined && { color }),
          ...(title !== undefined && { title }),
          ...(emoji !== undefined && { emoji }),
          ...(category !== undefined && { category }),
          ...(labels !== undefined && { labels }),
          ...(whoLabels !== undefined && { whoLabels }),
        },
      };
    },
  });

  const updateFeed = (
    {
      feedId,
      color,
      title,
      emoji,
      categoryId,
      labelIds,
      whoLabelIds,
    }: {
      feedId: string;
      color?: string | null;
      title?: string | null;
      emoji?: string | null;
      categoryId?: string | null;
      labelIds?: string[];
      whoLabelIds?: string[];
    },
    options?: MutateOptions<
      UpdateFeedForExperienceMutation,
      QueryError,
      UpdateFeedForExperienceMutationVariables
    >
  ) => {
    mutate(
      {
        input: {
          experienceId: experience.id,
          feedId,
          color,
          title,
          emoji,
          categoryId,
          labelIds,
          whoLabelIds,
        },
      },
      options
    );
  };

  return { updateFeed, isPending };
};
